import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiShoppingCart, FiShoppingBag, FiFileText, FiCheckCircle, FiDollarSign, FiClock, FiTag } from 'react-icons/fi';

// Helper function to format dates with time
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }) + ' • ' + date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// Format currency to INR
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const OrderHistory = ({ studentId }) => {
  const [orders, setOrders] = useState([]);
  const [ordersWithContent, setOrdersWithContent] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [cartOrdersWithContent, setCartOrdersWithContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('completed'); // 'completed' or 'cart'

  // Fetch all orders for the student
  useEffect(() => {
    const fetchOrders = async () => {
      if (!studentId) return;

      setLoading(true);
      try {
        // Fetch completed orders
        const ordersResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/StudentOrders/student/${studentId}`);
        setOrders(ordersResponse.data);

        // Fetch cart orders
        const cartResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/StudentOrders/student/${studentId}/cart`);
        setCartOrders(cartResponse.data);

        setError(null);
      } catch (err) {
        console.error('Error fetching student orders:', err);
        setError('Failed to load order history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [studentId]);

  // Fetch content details for each completed order
  useEffect(() => {
    const fetchContentDetails = async () => {
      if (orders.length === 0) return;

      try {
        const ordersWithContentDetails = await Promise.all(
          orders.map(async (order) => {
            try {
              const contentResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/contents/${order.content_id}`);
              return {
                ...order,
                content: contentResponse.data
              };
            } catch (contentError) {
              console.error(`Error fetching content for order ${order.order_id}:`, contentError);
              return {
                ...order,
                content: { title: 'Content information unavailable' }
              };
            }
          })
        );

        setOrdersWithContent(ordersWithContentDetails);
      } catch (err) {
        console.error('Error fetching content details for completed orders:', err);
      }
    };

    fetchContentDetails();
  }, [orders]);

  // Fetch content details for each cart item
  useEffect(() => {
    const fetchCartContentDetails = async () => {
      if (cartOrders.length === 0) return;

      try {
        const cartWithContentDetails = await Promise.all(
          cartOrders.map(async (order) => {
            try {
              const contentResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/contents/${order.content_id}`);
              return {
                ...order,
                content: contentResponse.data
              };
            } catch (contentError) {
              console.error(`Error fetching content for cart item ${order.order_id}:`, contentError);
              return {
                ...order,
                content: { title: 'Content information unavailable' }
              };
            }
          })
        );

        setCartOrdersWithContent(cartWithContentDetails);
      } catch (err) {
        console.error('Error fetching content details for cart items:', err);
      }
    };

    fetchCartContentDetails();
  }, [cartOrders]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          <span className="ml-2 text-gray-600">Loading order history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="text-red-500 py-4">{error}</div>
      </div>
    );
  }

  // Render empty state if no orders
  const renderEmptyState = (type) => (
    <div className="text-center py-8">
      <div className="flex justify-center mb-2">
        {type === 'completed' ? (
          <FiShoppingBag className="h-12 w-12 text-gray-300" />
        ) : (
          <FiShoppingCart className="h-12 w-12 text-gray-300" />
        )}
      </div>
      <h3 className="text-lg font-medium text-gray-500">
        {type === 'completed' 
          ? 'No completed orders found' 
          : 'No items in cart'}
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        {type === 'completed'
          ? 'When the student completes an order, it will appear here.'
          : 'When the student adds items to cart, they will appear here.'}
      </p>
    </div>
  );

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Order History</h2>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            className={`py-2 px-4 font-medium text-sm ${
              activeTab === 'completed'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => handleTabChange('completed')}
          >
            <div className="flex items-center">
              <FiCheckCircle className="mr-2" />
              Completed Orders
            </div>
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm ${
              activeTab === 'cart'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => handleTabChange('cart')}
          >
            <div className="flex items-center">
              <FiShoppingCart className="mr-2" />
              Cart Items {cartOrders.length > 0 && (
                <span className="ml-1 bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {cartOrders.length}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'completed' && (
          <div>
            {ordersWithContent.length === 0 ? (
              renderEmptyState('completed')
            ) : (
              <div className="space-y-2">
                {ordersWithContent.map((order) => (
                  <div key={order.order_id} className="border rounded-lg overflow-hidden bg-gray-50">
                    <div className="bg-indigo-50 px-3 py-2 border-b flex justify-between items-center">
                      <div className="flex items-center">
                        <FiShoppingBag className="text-indigo-500 mr-2" />
                        <span className="font-medium text-indigo-700 text-sm">
                          Order #{order.order_id}
                        </span>
                      </div>
                      <span className="text-xs text-gray-600">
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                    
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{order.content?.title || 'Product Title'}</h3>
                        {order.content?.thumbnail && (
                          <img 
                            src={order.content.thumbnail} 
                            alt={order.content.title}
                            className="w-12 h-12 object-cover rounded" 
                          />
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-gray-200 text-sm">
                        <div className="flex items-center">
                          <FiDollarSign className="text-green-500 mr-1" />
                          <div>
                            <p className="text-xs text-gray-500">Amount</p>
                            <p className="font-medium">{formatCurrency(order.amount_paid)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <FiFileText className="text-indigo-500 mr-1" />
                          <div>
                            <p className="text-xs text-gray-500">Method</p>
                            <p className="font-medium capitalize">{order.payment_method}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'cart' && (
          <div>
            {cartOrdersWithContent.length === 0 ? (
              renderEmptyState('cart')
            ) : (
              <div className="space-y-2">
                {cartOrdersWithContent.map((item) => (
                  <div key={item.order_id} className="border rounded-lg overflow-hidden bg-gray-50">
                    <div className="bg-orange-50 px-3 py-2 border-b flex justify-between items-center">
                      <div className="flex items-center">
                        <FiShoppingCart className="text-orange-500 mr-2" />
                        <span className="font-medium text-orange-700 text-sm">
                          Cart Item #{item.order_id}
                        </span>
                      </div>
                      <span className="text-xs text-gray-600">
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                    
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{item.content?.title || 'Product Title'}</h3>
                        {item.content?.thumbnail && (
                          <img 
                            src={item.content.thumbnail} 
                            alt={item.content.title}
                            className="w-12 h-12 object-cover rounded" 
                          />
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-gray-200 text-sm">
                        <div className="flex items-center">
                          <FiTag className="text-green-500 mr-1" />
                          <div>
                            <p className="text-xs text-gray-500">Price</p>
                            <p className="font-medium">{item.content?.sell_price ? formatCurrency(item.content.sell_price) : 'N/A'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <FiClock className="text-orange-500 mr-1" />
                          <div>
                            <p className="text-xs text-gray-500">Status</p>
                            <p className="font-medium">Pending Checkout</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;